/* =====================================================================
 * Series Navigation — interactive
 * Vanilla JS, no library. Loaded once, attaches to all instances on the page.
 * Behavior:
 *   - desktop carousel: prev/next arrows, scroll-driven progress bar, button disabled state
 *   - mobile carousel : same as desktop, recalc slide width on resize
 *   - mobile dropdown : renders toggle + list once per section on mobile
 *   - mobile stack    : nothing to do (CSS handles it)
 *   - single-select filter (v1.3 · AJAX clean-URL mode):
 *       * URL bar shows one active value, e.g. `?filter_franchise=Brightening`
 *       * Internal fetch uses Shopify-recognized `filter.p.m.<ns>.<key>`
 *         via the Section Rendering API (`/collections/<handle>?sections=...`)
 *       * Click toggles the handle in the visible filter key, AJAX-swaps the
 *         product DOM, then `history.replaceState` rewrites the URL bar
 *       * If AJAX fails, native navigation kicks in as a fallback
 * ===================================================================== */

(function () {
  if (window.__seriesNavLoaded) return;
  window.__seriesNavLoaded = true;

  function isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  function initOne(sectionEl) {
    if (sectionEl.dataset.seriesNavInit === '1') return;
    sectionEl.dataset.seriesNavInit = '1';

    var sectionId = sectionEl.dataset.sectionId;
    var desktopLayout = sectionEl.dataset.desktopLayout;
    var mobileLayout = sectionEl.dataset.mobileLayout;
    var cardStyle = sectionEl.dataset.cardStyle || 'compact';
    var desktopVisible = parseInt(sectionEl.dataset.desktopVisible, 10) || 5;
    var mobileVisible = parseInt(sectionEl.dataset.mobileVisible, 10) || 2;

    var viewport = sectionEl.querySelector('[data-viewport]');
    var track = sectionEl.querySelector('[data-track]');
    var prevBtn = sectionEl.querySelector('[data-prev]');
    var nextBtn = sectionEl.querySelector('[data-next]');
    var progressBar = sectionEl.querySelector('[data-progress-bar]');
    var controls = sectionEl.querySelector('[data-controls]');

    if (!viewport || !track) return;

    var slides = track.querySelectorAll('[data-slide]');
    if (slides.length === 0) return;

    // ---- current layout config ----
    function currentLayout() {
      return isDesktop() ? desktopLayout : mobileLayout;
    }
    function currentVisible() {
      return isDesktop() ? desktopVisible : mobileVisible;
    }

    // ---- recalc progress & button state ----
    function updateOverflowState() {
      var hasOverflow = track.scrollWidth - track.clientWidth > 1;
      sectionEl.classList.toggle('has-overflow', hasOverflow);
      return hasOverflow;
    }

    function updateProgress() {
      if (!progressBar) return;
      var max = track.scrollWidth - track.clientWidth;
      updateOverflowState();
      if (max <= 0) {
        progressBar.style.width = '100%';
      } else {
        var pct = Math.min(100, Math.max(0, (track.scrollLeft / max) * 100));
        progressBar.style.width = pct + '%';
      }
    }

    function updateArrows() {
      if (!prevBtn || !nextBtn) return;
      var max = track.scrollWidth - track.clientWidth;
      updateOverflowState();
      if (max <= 0) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }
      var sl = track.scrollLeft;
      // tolerate sub-pixel rounding
      prevBtn.disabled = sl <= 1;
      nextBtn.disabled = sl >= max - 1;
    }

    function shouldShowControls() {
      var layout = currentLayout();
      if (isDesktop()) return layout === 'carousel';
      return layout === 'carousel';
    }

    function showOrHideControls() {
      if (!controls) return;
      controls.style.display = shouldShowControls() ? 'flex' : 'none';
    }

    // ---- slide sizing based on visible count ----
    function sizeSlides() {
      var layout = currentLayout();

      // Visual image cards use fixed, CSS-driven widths so the row can stay
      // centered on desktop when all cards fit in the viewport.
      if (cardStyle === 'visual') {
        track.style.removeProperty('--sn-slide-w');
        sectionEl.style.removeProperty('--sn-current-visible');
        [].forEach.call(slides, function (slide) {
          slide.style.flex = '';
        });
        window.requestAnimationFrame(function () {
          updateProgress();
          updateArrows();
        });
        return;
      }

      // stack mode on mobile is fully CSS-driven — clear inline width
      if (!isDesktop() && layout === 'stack') {
        [].forEach.call(slides, function (slide) {
          slide.style.flex = '';
        });
        return;
      }

      // grid mode on desktop — let CSS handle flex-basis so cards wrap
      if (isDesktop() && layout === 'grid') {
        [].forEach.call(slides, function (slide) {
          slide.style.flex = '';
        });
        return;
      }

      var visible = currentVisible();
      var gap = 12;
      var viewportWidth = viewport.clientWidth;
      var slideWidth =
        visible >= slides.length
          ? viewportWidth / slides.length // all fit, distribute equally
          : (viewportWidth - gap * (visible - 1)) / visible;

      track.style.setProperty('--sn-slide-w', slideWidth + 'px');
      sectionEl.style.setProperty('--sn-current-visible', visible);

      // explicit width on each slide helps flex-basis behave in Safari/Edge
      [].forEach.call(slides, function (slide) {
        slide.style.flex = '0 0 ' + slideWidth + 'px';
      });
    }

    // ---- scroll by N visible ----
    function scrollByVisible(dir) {
      var visible = currentVisible();
      var step = viewport.clientWidth;
      track.scrollBy({ left: dir * step * 0.85, behavior: 'smooth' });
    }

    // ---- wire up ----
    if (prevBtn) prevBtn.addEventListener('click', function () { scrollByVisible(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollByVisible(1); });

    var rafScheduled = false;
    function onScroll() {
      if (rafScheduled) return;
      rafScheduled = true;
      window.requestAnimationFrame(function () {
        updateProgress();
        updateArrows();
        rafScheduled = false;
      });
    }
    track.addEventListener('scroll', onScroll, { passive: true });

    // ---- dropdown for mobile ----
    var ddRoot = null;
    function buildDropdownIfNeeded() {
      if (mobileLayout !== 'dropdown') return;
      if (isDesktop()) {
        // remove any dropdown if we had one
        if (ddRoot) {
          ddRoot.parentNode.removeChild(ddRoot);
          ddRoot = null;
        }
        return;
      }
      if (ddRoot) return;

      ddRoot = document.createElement('div');
      ddRoot.className = 'series-nav__mobile-select';
      ddRoot.setAttribute('aria-expanded', 'false');

      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'series-nav__mobile-toggle';
      toggle.textContent = sectionEl
        .querySelector('.series-nav__title')
        ?.textContent?.trim() || 'Jump to';

      var list = document.createElement('ul');
      list.className = 'series-nav__mobile-list';

      [].forEach.call(slides, function (slide) {
        var a = slide.querySelector('a');
        if (!a) return;
        var li = document.createElement('li');
        var link = document.createElement('a');
        link.href = a.href;
        link.textContent = a.textContent.trim().split('\n')[0];
        if (a.target) link.target = a.target;
        if (a.rel) link.rel = a.rel;
        li.appendChild(link);
        list.appendChild(li);
      });

      ddRoot.appendChild(toggle);
      ddRoot.appendChild(list);

      toggle.addEventListener('click', function () {
        var open = ddRoot.getAttribute('aria-expanded') === 'true';
        ddRoot.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
      document.addEventListener('click', function (e) {
        if (!ddRoot.contains(e.target)) {
          ddRoot.setAttribute('aria-expanded', 'false');
        }
      });

      var inner = sectionEl.querySelector('.series-nav__inner');
      inner.appendChild(ddRoot);
    }

    // ---- resize / orientation handling ----
    var resizeRaf = 0;
    function onResize() {
      if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
      resizeRaf = window.requestAnimationFrame(function () {
        sizeSlides();
        showOrHideControls();
        updateProgress();
        updateArrows();
        buildDropdownIfNeeded();
      });
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    // ---- Single-select filter (AJAX clean-URL mode · v1.3) ----
    function initSingleSelectFilter() {
      var filterableCards = track.querySelectorAll(
        'a.series-nav__card.is-filterable[data-filter-handle]'
      );
      if (!filterableCards.length) return;

      // Defaults sampled from the first filterable card; all cards in the
      // section share the same metafield + base path so this is safe.
      var sample = filterableCards[0];
      var filterCategory = sample.dataset.filterCategory || 'filter_franchise';
      var mfNs = sample.dataset.metafieldNamespace || 'custom';
      var mfKey = sample.dataset.metafieldKey || 'custom_product_line';
      var configuredProductSectionId = sample.dataset.productSectionId || '';
      var productSectionId = configuredProductSectionId || 'main-collection-products';
      var collectionHandle = sample.dataset.collectionHandle || '';
      var basePath = sample.dataset.basePath || window.location.pathname;

      var internalFilterKey = 'filter.p.m.' + mfNs + '.' + mfKey;
      var legacyInternalFilterKey = 'filter.v.option.' + mfNs + '.' + mfKey;

      function getAutoProductSectionId() {
        var facet = document.querySelector('product-facet[section-id]');
        var id = facet && facet.getAttribute('section-id');
        return id || '';
      }

      function getAllActiveValuesFromSearch(searchParams) {
        var values = searchParams.getAll(filterCategory);
        if (values.length) return values;
        values = searchParams.getAll(internalFilterKey);
        if (values.length) return values;
        return searchParams.getAll(legacyInternalFilterKey);
      }

      function getActiveValuesFromSearch(searchParams) {
        return getAllActiveValuesFromSearch(searchParams).slice(0, 1);
      }

      function syncActiveCards(values) {
        [].forEach.call(filterableCards, function (card) {
          var handle = card.dataset.filterHandle;
          var active = handle && values.indexOf(handle) !== -1;
          card.classList.toggle('is-active', !!active);
          if (active) {
            card.setAttribute('aria-pressed', 'true');
          } else {
            card.removeAttribute('aria-pressed');
          }
        });
      }

      function removeSeriesFilterParams(params) {
        params.delete(filterCategory);
        params.delete(internalFilterKey);
        params.delete(legacyInternalFilterKey);
        params.delete('page');
      }

      function buildInternalParams(values) {
        var params = new URLSearchParams(window.location.search);
        removeSeriesFilterParams(params);
        values.forEach(function (v) {
          params.append(internalFilterKey, v);
        });
        return params;
      }

      function buildCleanParams(values) {
        var params = new URLSearchParams(window.location.search);
        removeSeriesFilterParams(params);
        values.forEach(function (v) {
          params.append(filterCategory, v);
        });
        return params;
      }

      function getSectionHtml(data) {
        return data && data[productSectionId];
      }

      function parseSectionHtml(html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        return (
          parsed.querySelector('#shopify-section-' + productSectionId) ||
          parsed.querySelector('[data-section-id="' + productSectionId + '"]') ||
          parsed.querySelector('product-facet[section-id="' + productSectionId + '"]') ||
          parsed.querySelector('product-facet') ||
          parsed.querySelector('#facet-main')
        );
      }

      function fetchSection(values) {
        var params = buildInternalParams(values);
        var query = params.toString();
        var fetchUrl = '/collections/' + collectionHandle +
          '?sections=' + encodeURIComponent(productSectionId) +
          (query ? '&' + query : '');

        return fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (resp) {
            if (!resp.ok) throw new Error('HTTP ' + resp.status);
            return resp.json();
          })
          .then(function (data) {
            var html = getSectionHtml(data);
            if (!html) throw new Error('Empty section payload');
            return html;
          });
      }

      function getProductKey(item) {
        var link = item.querySelector('a[href*="/products/"]');
        if (link) {
          return link.getAttribute('href').split('?')[0];
        }
        return item.textContent.trim();
      }

      function reorderProductListByGroups(values, productContainer) {
        if (values.length < 2) return Promise.resolve();

        var liveList = productContainer.querySelector('.product-list__inner');
        if (!liveList) return Promise.resolve();

        return Promise.all(values.map(function (value) {
          return fetchSection([value]).then(function (html) {
            return { value: value, html: html };
          });
        })).then(function (groups) {
          var seen = {};
          var orderedItems = [];

          groups.forEach(function (group) {
            var section = parseSectionHtml(group.html);
            if (!section) return;

            var groupItems = section.querySelectorAll('.product-list__inner > product-item');
            [].forEach.call(groupItems, function (item) {
              var key = getProductKey(item);
              if (!key || seen[key]) return;
              seen[key] = true;
              orderedItems.push(item.cloneNode(true));
            });
          });

          if (!orderedItems.length) return;
          liveList.innerHTML = '';
          orderedItems.forEach(function (item) {
            liveList.appendChild(item);
          });
        });
      }

      function renderGroupedResults(values, productContainer) {
        if (!values.length) return Promise.resolve();

        return Promise.all(values.map(function (value) {
          return fetchSection([value]).then(function (html) {
            return { value: value, html: html };
          });
        })).then(function (groups) {
          if (!groups.length) return;

          var firstSection = parseSectionHtml(groups[0].html);
          if (firstSection) {
            productContainer.innerHTML = firstSection.innerHTML;
          }

          var liveList = productContainer.querySelector('.product-list__inner');
          if (!liveList) return;

          var seen = {};
          var orderedItems = [];

          groups.forEach(function (group) {
            var section = parseSectionHtml(group.html);
            if (!section) return;

            var groupItems = section.querySelectorAll('.product-list__inner > product-item');
            [].forEach.call(groupItems, function (item) {
              var key = getProductKey(item);
              if (!key || seen[key]) return;
              seen[key] = true;
              orderedItems.push(item.cloneNode(true));
            });
          });

          if (!orderedItems.length) return;
          liveList.innerHTML = '';
          orderedItems.forEach(function (item) {
            liveList.appendChild(item);
          });

          refreshProductList(productContainer);
        });
      }

      function refreshProductList(productContainer) {
        var productList = productContainer.querySelector('product-list');
        if (!productList) return;

        productList.productListInner = productList.querySelector('.product-list__inner');
        productList.productItems = [].slice.call(
          productList.querySelectorAll('product-item')
        );

        if (Object.prototype.hasOwnProperty.call(productList, '_animation')) {
          productList._animation = null;
        }

        if (
          productList.hasAttribute('stagger-apparition') &&
          !productList.hidden &&
          typeof productList._staggerProductsApparition === 'function'
        ) {
          productList._staggerProductsApparition();
        }
      }

      // 1) mark cards as active if their handle appears in current URL state
      var currentSearch = new URLSearchParams(window.location.search);
      var rawCurrentValues = getAllActiveValuesFromSearch(currentSearch);
      var currentValues = getActiveValuesFromSearch(currentSearch);
      var hasMultipleCurrentValues = rawCurrentValues.length > 1;
      syncActiveCards(currentValues);

      // Helper: detect if we should run AJAX mode (only on collection pages)
      function canAjax() {
        return !!collectionHandle;
      }

      // Helper: locate the product list container in the DOM
      function findProductContainer() {
        var autoProductSectionId = getAutoProductSectionId();
        if (autoProductSectionId && (!configuredProductSectionId || !document.querySelector('#shopify-section-' + productSectionId))) {
          productSectionId = autoProductSectionId;
        }

        // Shopify section ids look like: shopify-section-<id> (the id itself
        // is what we configure). We try a few selectors that cover common
        // themes (Dawn, Debut, Prestige, custom themes).
        var selectors = [
          '#shopify-section-' + productSectionId,
          '#' + productSectionId,
          '[data-section-id="' + productSectionId + '"]',
          'product-facet[section-id="' + productSectionId + '"]',
          '[data-section-type="main-collection"]',
          'product-facet',
          '#facet-main',
          '[id*="main-collection-products"]'
        ];
        for (var i = 0; i < selectors.length; i++) {
          var el = document.querySelector(selectors[i]);
          if (el) return el;
        }
        return null;
      }

      // Helper: toggle loading visual state
      var loadingCount = 0;
      function setLoading(on) {
        loadingCount += on ? 1 : -1;
        if (loadingCount < 0) loadingCount = 0;
        sectionEl.classList.toggle('is-loading', loadingCount > 0);
        if (on) {
          // Disable all filterable cards during load
          [].forEach.call(filterableCards, function (c) {
            c.classList.add('is-pending');
            c.setAttribute('aria-busy', 'true');
          });
        } else {
          [].forEach.call(filterableCards, function (c) {
            c.classList.remove('is-pending');
            c.removeAttribute('aria-busy');
          });
        }
      }

      // Core: rebuild product list via Shopify Section Rendering API and
      // update URL bar to the clean ?filter_franchise=... form.
      function applyFilter(nextValues, scrollTarget) {
        var productContainer = findProductContainer();
        if (!productContainer) {
          // Container not found → fall back to native navigation
          return false;
        }

        // Build the URL bar query (clean form)
        var cleanParams = buildCleanParams(nextValues);
        var cleanQuery = cleanParams.toString();

        setLoading(true);

        var renderPromise;
        if (nextValues.length > 1) {
          renderPromise = renderGroupedResults(nextValues, productContainer);
        } else {
          renderPromise = fetchSection(nextValues)
            .then(function (html) {
            // Swap product DOM. Some Shopify responses include the section
            // wrapper and some only include the section body, so parse first.
            var replacement = parseSectionHtml(html);
            productContainer.innerHTML = replacement ? replacement.innerHTML : html;
            refreshProductList(productContainer);
          });
        }

        renderPromise
          .then(function () {

            // Update URL bar (no page reload, no history entry)
            var newUrl = window.location.pathname + (cleanQuery ? '?' + cleanQuery : '');
            window.history.replaceState({}, '', newUrl);
            syncActiveCards(nextValues);

            // Scroll target if requested (e.g. to product grid)
            if (scrollTarget) {
              try {
                productContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } catch (e) { /* noop */ }
            }
          })
          .catch(function (err) {
            // Fallback: native navigation to the internal URL so the page
            // still works even if AJAX fails.
            var internalParams = buildInternalParams(nextValues);
            var fallbackQuery = internalParams.toString();
            window.location.href = basePath + (fallbackQuery ? '?' + fallbackQuery : '');
          })
          .then(function () {
            setLoading(false);
          });
        return true;
      }

      // 2) intercept clicks on filterable cards to switch the active filter
      [].forEach.call(filterableCards, function (card) {
        card.addEventListener('click', function (e) {
          if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
          if (typeof e.button === 'number' && e.button !== 0) return;

          var handle = card.dataset.filterHandle;
          if (!handle) return;

          e.preventDefault();

          // Single-select behavior: a new card replaces the previous
          // selection; clicking the selected card again clears the filter.
          var current = new URLSearchParams(window.location.search);
          var existing = getActiveValuesFromSearch(current);
          var nextValues;
          if (existing.indexOf(handle) !== -1) {
            nextValues = [];
          } else {
            nextValues = [handle];
          }

          if (!canAjax()) {
            // No collection handle → fallback to native navigation
            var fallbackParams = new URLSearchParams();
            nextValues.forEach(function (v) {
              fallbackParams.append(filterCategory, v);
            });
            var fallbackQuery = fallbackParams.toString();
            window.location.href = basePath + (fallbackQuery ? '?' + fallbackQuery : '');
            return;
          }

          applyFilter(nextValues, true);
        });
      });

      // 3) On initial page load, if URL has filter_franchise but products are
      //    not filtered (because Shopify doesn't recognize the clean key),
      //    do an AJAX re-render so the displayed products match the URL.
      if (currentValues.length > 0 && canAjax()) {
        // Detect whether Shopify rendered with the filter applied. The cheap
        // heuristic: count rendered product cards on the page. If 0, force
        // AJAX. Otherwise assume SSR worked (e.g. via a hidden helper).
        var renderedProducts = document.querySelectorAll(
          '#shopify-section-' + productSectionId + ' [data-section-type="product-card"],' +
          '#shopify-section-' + productSectionId + ' .product-card,' +
          '#shopify-section-' + productSectionId + ' .grid__item'
        );
        if (renderedProducts.length === 0 || hasMultipleCurrentValues) {
          applyFilter(currentValues, false);
        }
      }
    }

    // ---- initial ----
    showOrHideControls();
    sizeSlides();
    buildDropdownIfNeeded();
    updateProgress();
    updateArrows();
    initSingleSelectFilter();

    // ---- keyboard left / right ----
    sectionEl.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollByVisible(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollByVisible(1);
      }
    });

    // Expose for debugging (optional)
    sectionEl.__seriesNavAPI = {
      next: function () { scrollByVisible(1); },
      prev: function () { scrollByVisible(-1); },
      refresh: onResize
    };
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-section-type="series-navigation"]');
    [].forEach.call(sections, initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor: rerun on section load/unload
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var target = e.target.querySelector('[data-section-type="series-navigation"]')
                 || (e.target.matches && e.target.matches('[data-section-type="series-navigation"]') ? e.target : null);
      if (target) initOne(target);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var target = e.target.querySelector('[data-section-type="series-navigation"]')
                 || (e.target.matches && e.target.matches('[data-section-type="series-navigation"]') ? e.target : null);
      if (target) target.dataset.seriesNavInit = '';
    });
  }
})();
